import { useEffect, useMemo, useState } from "react";
import useSession from "hooks/useSession.ts";
import { FilterOpts } from "pages/OrderPages/OrderListPage/OrderListPage.tsx";
import {
	CacheKeys,
	InitialClosedLeadFilterOptions,
	InitialLeadDateFilterOption,
	InitialOpenLeadFilterOptions,
	InitialUnassignedLeadFilterOptions
} from "pages/OrderPages/OrderListPage/orderListConstants.ts";
import { useGetLeadsQuery } from "features/LeadGen/api";
import { useGetUserDetailsQuery } from "features/api/userApi.ts";
import { UserPermission } from "types/declarations/enums.ts";
import { LeadAssignmentsContent, LeadAssignmentsToolbar } from "./components";
import { useLeadFilters } from "./hooks";
import { isLeadUnassigned, leadIsClosed, LeadDateFilterOption, leadMatchesDateFilter } from "./utils";
import * as Text from "./LeadAssignmentsPage.text";
import {
	LeadAssignmentsPageContainer,
	LeadAssignmentsPageContent,
	LeadAssignmentsPageContentContainer,
	LeadAssignmentsPageHeader
} from "features/LeadGen/pages/LeadAssignments/LeadAssignmentsPage.styles.ts";
import { LeadAssignmentsTab } from "./components/LeadAssignmentsTabs/LeadAssignmentsTabs.tsx";

export const LeadAssignmentsPage = () => {
	const [currentTab, setCurrentTab] = useState<LeadAssignmentsTab>("open");
	const [searchValue, setSearchValue] = useState<string>("");

	const [openLeadFilterOptions, setOpenLeadFilterOptions] = useSession<FilterOpts>(
		CacheKeys.OpenLeadFilters,
		InitialOpenLeadFilterOptions
	);
	const [unassignedLeadFilterOptions, setUnassignedLeadFilterOptions] = useSession<FilterOpts>(
		CacheKeys.UnassignedLeadFilters,
		InitialUnassignedLeadFilterOptions
	);
	const [closedLeadFilterOptions, setClosedLeadFilterOptions] = useSession<FilterOpts>(
		CacheKeys.ClosedLeadFilters,
		InitialClosedLeadFilterOptions
	);
	const [openLeadDateFilter, setOpenLeadDateFilter] = useSession<LeadDateFilterOption>(
		CacheKeys.OpenLeadDateFilter,
		InitialLeadDateFilterOption
	);
	const [unassignedLeadDateFilter, setUnassignedLeadDateFilter] = useSession<LeadDateFilterOption>(
		CacheKeys.UnassignedLeadDateFilter,
		InitialLeadDateFilterOption
	);
	const [closedLeadDateFilter, setClosedLeadDateFilter] = useSession<LeadDateFilterOption>(
		CacheKeys.ClosedLeadDateFilter,
		InitialLeadDateFilterOption
	);

	const { data: leadsApiData, isLoading: isLeadsLoading } = useGetLeadsQuery();
	const { data: userDetails } = useGetUserDetailsQuery();

	const isAdmin = userDetails?.permissions?.includes(UserPermission.ADMIN) ?? false;
	const openTabLeads = useMemo(
		() => leadsApiData?.filter((lead) => !leadIsClosed(lead.status)) ?? [],
		[leadsApiData]
	);
	const unassignedTabLeads = useMemo(() => openTabLeads.filter(isLeadUnassigned), [openTabLeads]);
	const closedTabLeads = useMemo(
		() => leadsApiData?.filter((lead) => leadIsClosed(lead.status)) ?? [],
		[leadsApiData]
	);

	useEffect(() => {
		if (!isAdmin && currentTab === "unassigned") {
			setCurrentTab("open");
		}
	}, [currentTab, isAdmin]);

	const currentLeads = useMemo(() => {
		if (currentTab === "unassigned") {
			return unassignedTabLeads.filter((lead) => leadMatchesDateFilter(lead, unassignedLeadDateFilter));
		}

		if (currentTab === "closed") {
			return closedTabLeads.filter((lead) => leadMatchesDateFilter(lead, closedLeadDateFilter));
		}

		return openTabLeads.filter((lead) => leadMatchesDateFilter(lead, openLeadDateFilter));
	}, [
		closedLeadDateFilter,
		closedTabLeads,
		currentTab,
		openLeadDateFilter,
		openTabLeads,
		unassignedLeadDateFilter,
		unassignedTabLeads
	]);

	const hasCurrentTabLeads = useMemo(() => {
		if (currentTab === "unassigned") return unassignedTabLeads.length > 0;
		if (currentTab === "closed") return closedTabLeads.length > 0;
		return openTabLeads.length > 0;
	}, [closedTabLeads.length, currentTab, openTabLeads.length, unassignedTabLeads.length]);

	let currentFilterOptions = openLeadFilterOptions;
	let setCurrentFilterOptions = setOpenLeadFilterOptions;
	let currentDateFilter = openLeadDateFilter;
	let setCurrentDateFilter = setOpenLeadDateFilter;

	if (currentTab === "unassigned") {
		currentFilterOptions = unassignedLeadFilterOptions;
		setCurrentFilterOptions = setUnassignedLeadFilterOptions;
		currentDateFilter = unassignedLeadDateFilter;
		setCurrentDateFilter = setUnassignedLeadDateFilter;
	} else if (currentTab === "closed") {
		currentFilterOptions = closedLeadFilterOptions;
		setCurrentFilterOptions = setClosedLeadFilterOptions;
		currentDateFilter = closedLeadDateFilter;
		setCurrentDateFilter = setClosedLeadDateFilter;
	}

	const { filteredLeads, searchOptions } = useLeadFilters(currentLeads, currentFilterOptions, searchValue);

	const hasLeads = !!leadsApiData?.length;
	const showToolbar = hasLeads && !isLeadsLoading;

	const handleTabToggle = () => {
		setCurrentTab((prev) => (prev === "open" ? "closed" : "open"));
	};

	const handleSearch = (_e: unknown, value: string | null) => {
		setSearchValue(value || "");
	};

	return (
		<LeadAssignmentsPageContainer>
			<LeadAssignmentsPageHeader title={Text.MyLeadsText} />
			<LeadAssignmentsPageContentContainer>
				<LeadAssignmentsPageContent>
					{showToolbar && (
						<LeadAssignmentsToolbar
							currentTab={currentTab}
							isAdmin={isAdmin}
							handleTabChange={setCurrentTab}
							handleTabToggle={handleTabToggle}
							filterOptions={currentFilterOptions}
							setFilterOptions={setCurrentFilterOptions}
							leads={currentLeads}
							searchOptions={searchOptions}
							searchValue={searchValue}
							handleSearch={handleSearch}
							selectedDateFilter={currentDateFilter}
							handleDateFilterChange={setCurrentDateFilter}
						/>
					)}
					<LeadAssignmentsContent
						isLoading={isLeadsLoading}
						hasLeads={hasLeads}
						hasCurrentTabLeads={hasCurrentTabLeads}
						currentTab={currentTab}
						filteredLeads={filteredLeads}
					/>
				</LeadAssignmentsPageContent>
			</LeadAssignmentsPageContentContainer>
		</LeadAssignmentsPageContainer>
	);
};
